import React from "react";
import { Shield, Cpu, Users, Zap, ArrowRight, Bot, Brain, MessageSquare, Clock } from "lucide-react";

const GhostAssistantSection = ({ isVisible }) => {
  const features = [
    {
      icon: Brain,
      title: "Intelligent Automation",
      description: "Advanced AI-powered workflows that streamline operations across all Prestigious divisions.",
    },
    {
      icon: MessageSquare,
      title: "Unified Communications",
      description: "Seamless integration across teams, clients, and partners with smart routing and prioritization.",
    },
    {
      icon: Clock,
      title: "24/7 Availability",
      description: "Always-on support ensuring no opportunity or request goes unanswered.",
    },
    {
      icon: Shield,
      title: "Enterprise Security",
      description: "Military-grade encryption and compliance built into every interaction.",
    },
  ];

  return (
    <section
      id="ghost"
      className={`section-fade py-24 sm:py-32 bg-[#0A192F] relative overflow-hidden ${isVisible ? "visible" : ""}`}
      data-testid="ghost-section"
    >
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#D4AF37]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full blur-2xl" />
        {/* Grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(white 1px, transparent 1px), linear-gradient(90deg, white 1px, transparent 1px)`,
            backgroundSize: "40px 40px",
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <div className="space-y-8">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full">
              <Cpu className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-xs font-semibold text-white tracking-wide uppercase">
                Proprietary Technology
              </span>
            </div>

            {/* Heading */}
            <div>
              <h2 className="text-4xl sm:text-5xl font-medium text-white tracking-tight font-['Outfit'] mb-4">
                Ghost™ Assistant
              </h2>
              <p className="text-lg text-white/80 leading-relaxed">
                <span className="text-[#D4AF37] font-medium">Powered by Prestigious Companies</span> — 
                an advanced AI platform built on a foundation of service, discipline, and the 
                unwavering commitment to helping others move forward.
              </p>
            </div>

            {/* Story/Tribute */}
            <div className="relative pl-6 border-l-2 border-[#D4AF37]">
              <p className="text-white/70 italic leading-relaxed">
                "Ghost Assistant is a tribute to those who have served and sacrificed. 
                Built with the same discipline and dedication that defines our military heroes, 
                this platform carries forward their legacy of service — empowering businesses 
                and communities to achieve more, together."
              </p>
            </div>

            {/* Features Grid */}
            <div className="grid sm:grid-cols-2 gap-4 pt-4">
              {features.map((feature) => (
                <div
                  key={feature.title}
                  className="group p-5 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-[#D4AF37]/30 transition-all duration-300"
                  data-testid={`ghost-feature-${feature.title.toLowerCase().replace(" ", "-")}`}
                >
                  <div className="w-10 h-10 bg-[#D4AF37]/20 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#D4AF37]/30 transition-colors">
                    <feature.icon className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <h3 className="text-white font-semibold font-['Outfit'] mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-white/60 text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="flex flex-wrap gap-4 pt-4">
              <button
                onClick={() => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })}
                className="btn-gold group"
                data-testid="ghost-contact-btn"
              >
                Learn More
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          </div>

          {/* Ghost Logo/Visual */}
          <div className="relative flex justify-center items-center">
            {/* Glow Effect */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-80 h-80 bg-[#D4AF37]/20 rounded-full blur-3xl animate-pulse" />
            </div>
            
            {/* Logo Container */}
            <div className="relative ghost-glow">
              <div className="w-80 h-80 sm:w-96 sm:h-96 rounded-3xl bg-gradient-to-br from-[#112240] to-[#0A192F] border border-white/10 shadow-2xl flex items-center justify-center overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_company-collective/artifacts/m6g4qkkl_image.png"
                  alt="Ghost Assistant"
                  className="w-64 h-64 sm:w-72 sm:h-72 object-contain"
                  data-testid="ghost-logo"
                />
              </div>
              
              {/* Floating badges */}
              <div className="absolute -top-4 -right-4 px-4 py-2 bg-[#D4AF37] rounded-full shadow-lg">
                <span className="text-xs font-bold text-[#0A192F] uppercase tracking-wide">AI Powered</span>
              </div>
              
              <div className="absolute -bottom-4 -left-4 px-4 py-2 bg-white rounded-full shadow-lg">
                <span className="text-xs font-bold text-[#0A192F] uppercase tracking-wide">Enterprise Ready</span>
              </div>
            </div>

            {/* Stats */}
            <div className="absolute -right-8 top-1/2 -translate-y-1/2 hidden xl:flex flex-col gap-4">
              {[
                { value: "99.9%", label: "Uptime" },
                { value: "<1s", label: "Response" },
                { value: "256-bit", label: "Encryption" },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="px-4 py-3 bg-white/10 backdrop-blur rounded-lg border border-white/10"
                >
                  <p className="text-xl font-bold text-[#D4AF37] font-['Outfit']">{stat.value}</p>
                  <p className="text-xs text-white/60">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GhostAssistantSection;
